import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  get,
  isEmpty
} from 'lodash';
import moment from 'moment';
import {
  ELSCommonUIConstants,
  ELSLoggingService,
  ELSPageLoader,
  ELSTokenHelper,
  ELSTokenServiceRegistrar
} from '@els/els-ui-common-react';
import { useHistory } from 'react-router';
import { RoutePath } from '../../components/app/app.constants';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import {
  AppLinkCookies,
  AppLinkData
} from '../../apis/eols-app-link/eols-app-link.dtos';
import {
  AppLinkRedirectErrorMessage,
  AppLinkRedirectErrorMessages
} from '../../constants/app-link-redirect.constants';
import { ELSTokenUser } from '../../models/els.dtos';
import { AppContext } from '../../components/app/AppProvider.component';
import { getQueryParam } from '../../utilities/location.utilities';
import {
  fetchAllAppFeatureFlagsAction,
  fetchAppLinkDataAction,
  fetchCourseSectionAction,
  fetchEolsUserAction,
  fetchEvolveUsersAction,
  fetchHashLinkAction,
  fetchIpAddressAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import { addSearchParams } from '../../utilities/app.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { RecordObject } from '../../models/common.models';
import DefaultAppState from '../../components/app/app-state.constants';

type AppLinkMethodProps = AppLinkCookies;

export type AppLinkRedirectState = {
  hasError: boolean;
  errorMessage: string;
}

const defaultState: AppLinkRedirectState = {
  hasError: false,
  errorMessage: ''
};

const fileName = 'AppLinkRedirect.page';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const AppLinkRedirectComponent = (): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  if (!appState.cookieCheckComplete) {
    return null;
  }

  const [state, setState] = useState<AppLinkRedirectState>(defaultState);
  const history = useHistory();

  const logError = (errorType: AppLinkRedirectErrorMessage, error): void => {
    const url = get(error, 'config.url', '');
    const method = get(error, 'config.method', '');
    const status = get(error, 'response.status', '');
    const errorMessage = AppLinkRedirectErrorMessages[errorType];

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.APP_LINK_ERROR,
        props: {
          type: errorType,
          url,
          method,
          status
        }
      }
    });
    ELSLoggingService.error(fileName, errorMessage);
    setState({
      hasError: true,
      errorMessage
    });
  };

  const handleAppLinkLaunch = (appLinkData: AppLinkData) => {

    setAppState((prevState) => {
      return {
        ...prevState,
        remediationBaseState: DefaultAppState.remediationBaseState
      }
    });

    if ((appLinkData.outPostBody as RecordObject)?.targetPage === 'ADMIN') {
      history.replace(RoutePath.ADMIN);
      return;
    }

    // Temporary PLP eval workaround (FLOR-4070): Sherpath launches flagged course sections here
    if ((appLinkData.outPostBody as RecordObject)?.targetPage === 'PLP_LIST') {
      history.replace(RoutePath.PLP_LIST);
      return;
    }

    if ((appLinkData.action === AppAction.ASSESSMENT_PERFORMANCE_VIEW)
      && appLinkData.targetApp === Application.REMEDIATION) {
      history.replace(RoutePath.REMEDIATION_STUDENT_DETAIL);
      return;
    }

    history.replace(RoutePath.REMEDIATION_STUDENT_HOME);
  };

  const handleAppLinkReturn = (appLinkData: AppLinkData) => {
    const body = appLinkData.outPostBody as RecordObject;
    if (body?.ref === RoutePath.REMEDIATION_STUDENT_HOME) {
      if (appLinkData.action === AppAction.ASSESSMENT_START && body?.recommendationAttemptId) {
        history.replace(addSearchParams(RoutePath.REMEDIATION_POST_PLAYER, {
          recommendationAttemptId: body.recommendationAttemptId,
        }));
        return;
      }
      if (appLinkData.action === AppAction.ASSESSMENT_PERFORMANCE_VIEW) {
        history.replace(addSearchParams(RoutePath.REMEDIATION_STUDENT_HOME, {
          isOpenAssessModal: true
        }));
      }
    }

    if (body?.ref === RoutePath.REMEDIATION_STUDENT_DETAIL) {
      history.replace(RoutePath.REMEDIATION_STUDENT_DETAIL);
    }
  };

  const launchApp = (config: AppLinkMethodProps) => {

    const { token, linkId } = config;

    ELSTokenServiceRegistrar.register(token);
    const tokenUser: ELSTokenUser = ELSTokenHelper.getUser();
    const courseSectionId: string = tokenUser.appParams?.courseId?.toString() || null;
    const ecrId: string = tokenUser.appParams?.ecrId?.toString() || null;

    setAppState((prevState) => {
      return {
        ...prevState,
        registeredToken: token,
        userId: tokenUser.userId.toString(),
        userRole: tokenUser.role,
        courseSectionId,
        tokenIsbns: tokenUser.appParams.isbns,
        selectedRaasAssessmentDto: null,
        ecrId
      };
    });

    fetchEvolveUsersAction({
      useCache: true,
      userId: tokenUser.userId.toString(),
      appState,
      appStateRef,
      setAppState
    });

    fetchEolsUserAction({
      useCache: true,
      userId: tokenUser.userId.toString(),
      appState,
      appStateRef,
      setAppState
    });

    fetchIpAddressAction({
      appState,
      appStateRef,
      setAppState
    });

    return fetchAppLinkDataAction({
      appLinkId: linkId,
      appState,
      appStateRef,
      setAppState,
    }).then((appLinkData: AppLinkData) => {

      return Promise.all([
        fetchAllAppFeatureFlagsAction({
          appState,
          appStateRef,
          setAppState,
          useCache: false
        }),
        fetchCourseSectionAction({
          courseSectionId,
          appState,
          appStateRef,
          setAppState,
          useCache: false
        })
      ])
        .then(() => {

          trackNewRelicAction({
            actionProps: {
              action: NewRelicAnalyticsAction.APP_LINK_LAUNCH,
              props: {
                linkType: appLinkData.action,
                srcApp: appLinkData.srcApp,
                targetApp: appLinkData.targetApp
              }
            },
            appStateRef,
          });

          if (appLinkData.targetApp === Application.REMEDIATION) {
            handleAppLinkLaunch(appLinkData);
            return Promise.resolve();
          }
          if (appLinkData.srcApp === Application.REMEDIATION) {
            handleAppLinkReturn(appLinkData);
            return Promise.resolve();
          }
          return Promise.resolve();
        });
    }).catch((error) => {
      logError(AppLinkRedirectErrorMessage.REQUEST_FAILED, error);
    });
  };

  const isRedirectValid = (config: AppLinkMethodProps): boolean => {

    const { token, linkId } = config;

    if (!token || !linkId) {
      logError(AppLinkRedirectErrorMessage.MISSING_COOKIES, null);
      return false;
    }

    const tokenExpireDate = ELSTokenHelper.getExpirationDateFromToken(token);
    const now = moment();
    if (now.isAfter(tokenExpireDate)) {
      logError(AppLinkRedirectErrorMessage.EXPIRED_TOKEN, null);
      return false;
    }

    const tokenUser: ELSTokenUser = ELSTokenHelper.getUserInfoFromToken(token);
    if (isEmpty(tokenUser)) {
      logError(AppLinkRedirectErrorMessage.INVALID_TOKEN, null);
      return false;
    }

    const validRoles = [
      ELSCommonUIConstants.userType.Student,
      ELSCommonUIConstants.userType.Instructor
    ];

    if (!validRoles.includes(tokenUser.role)) {
      logError(AppLinkRedirectErrorMessage.UNSUPPORTED_USER_ROLE, null);
      return false;
    }

    return true;
  };

  useEffect(() => {
    const linkHash = getQueryParam('linkHash');

    if (linkHash) {
      fetchHashLinkAction({
        linkHash,
        appState,
        appStateRef,
        setAppState
      }).then((appLinkCookies: AppLinkCookies) => {
        setAppState((prevState) => {
          return {
            ...prevState,
            appLinkCookies,
          };
        });
        launchApp(appLinkCookies);
      }).catch(() => {
        logError(AppLinkRedirectErrorMessage.REQUEST_FAILED, null);
      });
    } else if (isRedirectValid(appState.appLinkCookies)) {
      launchApp(appState.appLinkCookies);
    }
  }, []);

  if (state.hasError) {
    return (
      <div className="u-els-padding-3x">
        <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary">{state.errorMessage}</h2>
      </div>
    );
  }

  return (
    <div className="u-els-padding-3x">
      <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary"><ELSPageLoader /></h2>
    </div>
  );
};

const AppLinkRedirect = AppLinkRedirectComponent;
export default AppLinkRedirect;
